<template>
  <div class="container-fluid" id="about">
    <div class="row wrapper">
      <div class="col-12 col-md-4">
        <div class="header">
          <div id="steps" class="steps" tabindex="0" role="document">
            <span>Step 1</span> of 5
          </div>
          <h1 tabindex="0">About you</h1>
          <div class="hero-image" alt="About you"></div>
        </div>
      </div>

      <div class="col-12 col-md-8">
        <div class="main">
          <ValidationObserver v-slot="{ valid }" ref="form">
            <form @submit.prevent="onSubmit">
              <div class="form-group">
                <ValidationProvider mode="lazy" name="age" :rules="'required:'+pageContent.pages.about.outOfRangeErrorMessage+'|between:'+config.fields.age.min+','+config.fields.age.max+','+pageContent.pages.about.outOfRangeErrorMessage" v-slot="{ errors, failedRules }">
                  <label for="age" tabindex="0">Your age</label>
                  <cleave
                    data-analytics-capture-value="true"
                    :options="formatOptions.age"
                    class="form-control"
                    id="age"
                    name="age"
                    v-model.number="data.stepData.age"
                    type="text"
                    tabindex="0"
                    v-on:blur="updateLifeInsurancePaymentPeriod">
                  </cleave>
                  <span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }">{{ errors[0] }}</span>
                  <span v-if="failedRules.between && pageContent.pages.about.infoBoxError" v-html="pageContent.pages.about.infoBoxError"></span>
                </ValidationProvider>
              </div>

              <div class="form-group max">
                <ValidationProvider name="coupledep" :rules="'required:'+pageContent.pages.about.familyTypeRequiredErrorMessage" v-slot="{ errors }">
                  <label tabindex="0">Your family</label><br>
                  <div class="row no-gutters square-button-row">
                      <div class="col-12 col-lg-6">
                        <div class="btn-radio-square">
                          <input tabindex="0" type="radio" id="single" name="familyType" v-model="data.stepData.familyType" :value="Enum.familyType.SINGLE" @click="triggerHeightCheck()" data-analytics-capture-value="true">
                          <label for="single">
                            <i class="icon single"></i>
                            <span>Single</span>
                          </label>
                        </div>

                        <div class="btn-radio-square">
                          <input tabindex="0" type="radio" id="couple" name="familyType" v-model="data.stepData.familyType" :value="Enum.familyType.COUPLE" @click="triggerHeightCheck()" data-analytics-capture-value="true">
                          <label for="couple">
                            <i class="icon couple"></i>
                            <span>Couple</span>
                          </label>
                        </div>
                      </div>

                      <div class="col-12 col-lg-6">
                        <div class="btn-radio-square">
                          <input tabindex="0" type="radio" id="singledep" name="familyType" v-model="data.stepData.familyType" :value="Enum.familyType.SINGLE_DEPENDANT" @click="triggerHeightCheck()" data-analytics-capture-value="true">
                          <label for="singledep">
                            <i class="icon single-dependant"></i>
                            <span>Single with a dependant(s)</span>
                          </label>
                        </div>

                        <div class="btn-radio-square">
                          <input tabindex="0" type="radio" id="coupledep" name="familyType" v-model="data.stepData.familyType" :value="Enum.familyType.COUPLE_DEPENDANT" @click="triggerHeightCheck()" data-analytics-capture-value="true">
                          <label for="coupledep">
                            <i class="icon couple-dependant"></i>
                            <span>Couple with dependant(s)</span>
                          </label>
                        </div>
                      </div>
                  </div>
                  <span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }">{{ errors[0] }}</span>
                </ValidationProvider>
              </div>

              <div class="info-box" v-if="data.stepData.familyType === Enum.familyType.SINGLE">
                <div class="form-row">
                  <div class="col-1 icon"><i class="icon-info"></i></div>
                  <div class="col text">{{pageContent.pages.about.familyTypeSingle}}</div>
                </div>
              </div>
              <div class="info-box" v-if="data.stepData.familyType === Enum.familyType.COUPLE">
                <div class="form-row">
                  <div class="col-1 icon"><i class="icon-info"></i></div>
                  <div class="col text">{{pageContent.pages.about.familyTypeCouple}}</div>
                </div>
              </div>

              <div class="info-box" v-if="data.stepData.familyType === Enum.familyType.SINGLE_DEPENDANT">
                <div class="form-row">
                  <div class="col-1 icon"><i class="icon-info"></i></div>
                  <div class="col text">{{pageContent.pages.about.familyTypeCouple}}</div>
                </div>
              </div>
              <div class="info-box" v-if="data.stepData.familyType === Enum.familyType.COUPLE_DEPENDANT">
                <div class="form-row">
                  <div class="col-1 icon"><i class="icon-info"></i></div>
                  <div class="col text">{{pageContent.pages.about.familyTypeCouple}}</div>
                </div>
              </div>

              <!-- dependants -->
              <div class="form-group" v-if="data.stepData.familyType === Enum.familyType.SINGLE_DEPENDANT || data.stepData.familyType === Enum.familyType.COUPLE_DEPENDANT">
                <div class="row no-gutters">
                  <div class="col-auto"><h2 tabindex="0">Your dependants</h2></div>
                  <div class="col-auto">
                    <b-button id="tooltip-dependants" variant="tooltip" aria-label="tooltip dependants"><i class="icon-help"></i></b-button>
                    <b-tooltip target="tooltip-dependants" placement="top">
                      This calculator assumes that your dependants are children who financially depend on you up until the age of 18 (inclusive).
                    </b-tooltip>
                  </div>

                </div>
                <div class="row repeat-input" v-for="(dependant, ind) in dependants" :key="ind">
                  <div class="col">
                    <div class="row">
                      <div class="col"><label :for="'dependant_'+ind" tabindex="0">Age of dependant {{ind + 1}}</label></div>
                    </div>
                    <div class="row">
                      <div class="col">
                        <ValidationProvider mode="lazy" :name="'dependant_'+ind" :rules="'required:'+pageContent.pages.about.dependantAgeRangeErrorMessage+'|between:'+config.fields.dependantsAge.min+','+config.fields.dependantsAge.max+','+pageContent.pages.about.dependantAgeRangeErrorMessage" v-slot="{ errors }">
                          <cleave
                            data-analytics-capture-value="true"
                            tabindex="0"
                            :options="formatOptions.age"
                            class="form-control pre-remove-btn"
                            :id="'dependant_'+ind"
                            v-model.number="dependant.Age"
                            :value="dependant.Age"
                            v-on:blur="triggerHeightCheck"
                            type="text">
                          </cleave>
                          <button tabindex="0" v-if="ind != 0" type="button" class="remove-item-btn" v-on:click="removeDependant(ind)"><i class="icon-remove" aria-label="Remove dependant"></i></button>
                          <br/><span role="alert" class="form-text" v-bind:class="{ 'text-error': errors.length > 0 }" v-html="errors[0]"></span>
                        </ValidationProvider>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="row">
                  <div class="col">
                    <div class="info-box" v-if="dependants.length === 5">
                      <div class="form-row">
                        <div class="col-1 icon"><i class="icon-info"></i></div>
                        <div class="col text" v-html="pageContent.pages.about.dependantLimitInfoMessage"></div>
                      </div>
                    </div>
                    <button class="add-button" tabindex="0" type="button" v-on:click="addDependant" v-if="dependants.length < 5">+ Add dependant</button>
                  </div>
                </div>
              </div>
              <!-- end debts -->

              <div class="nav-button row" >
                <router-link class="assumption-button col" :to="{ name: 'Assumptions', params: {activeTab: 'assumptions' } }" tabindex="0" role="button"><i class="icon-edit"></i> Edit assumptions </router-link>
                <router-link class="back-button col" to="/" tabindex="0" role="button">Back </router-link>
                <button class="next-button col" v-bind:class="[valid ? 'is-active' : 'is-disabled']"  type="submit" tabindex="0" role="button">Next</button>
              </div>
            </form>
          </ValidationObserver>
        </div>
      </div>

    </div>
    <CustomFooter></CustomFooter>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { mapFields } from "vuex-map-fields";
import Cleave from "vue-cleave-component";

import { ValidationProvider, extend, ValidationObserver } from 'vee-validate';
import { required, between } from 'vee-validate/dist/rules';
import CustomFooter from '../components/CustomFooter';

import _ from 'lodash';

extend('required', {
  ...required,
  params: ['msg'],
  message: '{msg}'
});
extend('between', {
  ...between,
  params: ['min', 'max', 'msg'],
  validate(value, { min, max }) {
    return value >= min && value <= max;
  },
  message: '{msg}'
});

export default {
  name: 'Home',
  components: { Cleave, ValidationProvider, ValidationObserver, CustomFooter },
  computed: {
    ...mapFields(["data", "dependants"]),
    ...mapGetters(["Enum", "config", "formatOptions", "pageContent"]),
  },
  methods: {
    toggleFamilyTypeBtn() {
      this.triggerHeightCheck();
    },
    addDependant() {
      this.dependants.push({"Age": null, "Amount": 3000, "Error": false});
      this.triggerHeightCheck();
    },
    removeDependant(ind) {
      if(this.dependants.length > 1) {
        this.dependants.splice(ind, 1);
      }
      else {
        this.dependants.pop();
        this.dependants.push({"Age": null, "Amount": 3000, "Error": false});
      }
      this.triggerHeightCheck();
    },
    updateLifeInsurancePaymentPeriod: function() {
			this.$store.commit("setLifeInsurancePaymentPeriod");
      this.triggerHeightCheck();
		},
    triggerHeightCheck: function() {
      // Add slight delay otherwise incorrect height is read
      var rf = this;
      setTimeout(function() {
        rf.$refreshIframeHeight();
      }, 300);
    },
    onSubmit () {
      this.$refs.form.validateWithInfo().then(result => {
        if (!result.isValid) {
          var fieldArr = _.valuesIn(result.fields);
          var firstError = document.getElementById(fieldArr[0].id)
          if(firstError) {
            firstError.scrollIntoView({behavior: 'smooth'});
          }
          this.triggerHeightCheck();
          return;
        }
        this.$router.push('/debt');
      });
    },
  },
  mounted() {
    //load defaults
    this.triggerHeightCheck();
    this.$scrollIframeTop();
    window.addEventListener("resize", this.$refreshIframeHeight);
  },
  destroyed() {
    window.removeEventListener("resize", this.$refreshIframeHeight);
  },
  beforeDestroy() {
    // send analytics - complete form step
    var dataObj = {
      "age": this.data.stepData.age,
      "familyType": this.data.stepData.familyType,
      "dependants": []
    };
    if(this.data.stepData.familyType === this.Enum.familyType.SINGLE_DEPENDANT ||
      this.data.stepData.familyType === this.Enum.familyType.COUPLE_DEPENDANT) {
        this.dependants.forEach(dependant => {
          var dep = {"Age": dependant.Age}
          dataObj.dependants.push(dep);
          this.$gtag.event("step-about", {
            "event_category": "field-data",
            "event_label": "dependant age",
            "value": dependant.Age
          })
        });
    }
    this.$gtag.event("step-about", {
      "event_category": "field-data",
      "event_label": "age",
      "value": this.data.stepData.age
    })
    this.$gtag.event("step-about", {
      "event_category": "field-data",
      "event_label": "family type",
      "value": this.data.stepData.familyType
    })

    // clear any dependant errors when moving step. should not be any errors.
    // Was showing error on expenses page or if you went to next step and back to about step.
    this.dependants.forEach(dep => {
      dep.Error = false;
    });
  }
};
</script>
